import React from "react";
import { Download, Printer, ShieldCheck, ExternalLink, AlertTriangle, CheckCircle2, Info } from "lucide-react";
import { toast } from "sonner";

// Report contenuto (versione visualizzabile). Il file .md completo è in /app/REPORT_COOKIE_TRACKER_PERLA.md
const RAW_MD_URL = "/report-cookie-tracker-perla.md"; // static serving optional

const REPORT_MD = String.raw`# Report Cookie & Tracker — Perla Music

Documento tecnico per la redazione di Privacy Policy e Cookie Policy

Versione: 1.0 | Data: Marzo 2026 | Stato sito: Preview / Staging (pre-deploy)

---

## 1. Sintesi esecutiva

Il sito Perla Music NON installa cookie di profilazione o di analytics ad oggi.
La persistenza lato client è limitata a due chiavi tecnico-funzionali in localStorage,
entrambe esenti dall'obbligo di consenso preventivo ai sensi dell'art. 122 del Codice Privacy.

Uniche risorse di terze parti:
- Google Fonts (font Inter)
- YouTube embed (solo su pagine con video testimonianze pubblicati)

Previste e non ancora attive: Meta Ads Pixel, Google Calendar OAuth, WhatsApp Cloud API.

## 2. Cookie di prima parte

Cookie HTTP: nessuno. Il backend FastAPI non emette Set-Cookie.

localStorage:
- perla_token: JWT firmato per utenti loggati (admin/CRM). Necessario tecnico, esente da consenso.
  Durata: fino a logout o 30gg max.
- perla_lang: preferenza lingua (it/en). Funzionale, esente da consenso.
  Durata: persistente fino a cancellazione manuale.

I visitatori anonimi generano SOLO perla_lang e solo se cambiano lingua manualmente.

## 3. Terze parti che caricano risorse

Google Fonts (fonts.googleapis.com + fonts.gstatic.com):
- Font Inter (peso 600)
- Cookie diretti: nessuno
- Dato trasmesso: IP dell'utente ad ogni pageload
- Titolare terzo: Google Ireland Limited
- Base giuridica: interesse legittimo + menzione in Privacy Policy
- Mitigazione consigliata: self-hosting del font (~30 min)

YouTube embed (www.youtube.com/embed/...):
- Attivo solo se sono presenti testimonianze video
- Pagine coinvolte: /, /testimonianze
- Cookie installati direttamente da Google al load del player:
  VISITOR_INFO1_LIVE (6 mesi), YSC (sessione), PREF (2 anni),
  CONSENT (2 anni), __Secure-YEC, SOCS
- Categoria: profilazione / marketing terzi
- Base giuridica: consenso esplicito preventivo
- Mitigazione consigliata: usare youtube-nocookie.com (~5 min)

## 4. Analytics / marketing / pixel

Nessuno installato ad oggi.

Verificati assenti nel codice: Google Analytics, GA4, Google Tag Manager,
Meta Pixel, TikTok Pixel, LinkedIn Insight, Hotjar, Microsoft Clarity,
Mixpanel, Segment, Amplitude, Hubspot.

## 5. Integrazioni pianificate — non ancora attive

Meta Ads Pixel: cookie _fbp, _fbc + terze parti facebook.com.
  Richiederà consenso esplicito e banner CMP.

Google Calendar OAuth: solo per dipendenti CRM loggati.
  Nessun cookie sui visitatori pubblici.

WhatsApp Cloud API: server-to-server, nessun cookie sul client.
  Informativa specifica da aggiungere al form lead.

## 6. Punti di raccolta dati personali

Form Contatti (/contatti): nome, email, telefono, messaggio.

Landing 1 & 2 (/landing-1, /landing-2): nome artista, email, telefono,
genere, ascoltatori mensili, obiettivo, budget, IG, regione, messaggio +
UTM automatici (source/campaign/medium/ad/adset/content/term/platform).

CRM (area riservata): non è un punto pubblico. Accessibile solo a
dipendenti autenticati di Perla Music.

## 7. Storage e localizzazione

Tutti i dati sono in MongoDB self-hosted all'interno dell'infrastruttura
del sito. Nessun processor esterno oggi. Backup automatico giornaliero.

Trasferimento extra-UE oggi: nessuno.

## 8. Raccomandazioni prima del deploy

1. Self-hosting del font Inter (~30 min) — rimuove chiamata a Google Fonts
2. YouTube embed in modalità youtube-nocookie.com (~5 min)
3. Pagine /privacy-policy e /cookie-policy editabili da CMS (~1 ora)
4. Banner informativo cookie non vincolante (~1 ora)

## 9. Riepilogo per il DPO

Oggi (staging pre-deploy):
- Non serve banner cookie con consenso vincolante — no cookie profilazione attivi
- Serve informativa privacy con menzione di Google Fonts + YouTube
- Servono i punti 1-4 delle raccomandazioni implementati prima del go-live

Al momento dell'attivazione di Meta Pixel / WhatsApp diventerà obbligatorio
un banner CMP con consenso granulare e blocco preventivo dei tag terzi.

Fine del documento.`;

const SECTIONS = [
  {
    id: "summary",
    number: "1",
    title: "Sintesi esecutiva",
    body: (
      <div className="space-y-3 text-sm text-[#0A0A0A]/85 leading-relaxed">
        <p>Il sito Perla Music <strong>non installa cookie di profilazione o di analytics ad oggi</strong>. La persistenza lato client è limitata a due chiavi tecnico‑funzionali in <code className="bg-[#0A0A0A]/5 px-1">localStorage</code> (autenticazione area riservata e preferenza lingua), entrambe esenti dall'obbligo di consenso preventivo ai sensi dell'art. 122 del Codice Privacy.</p>
        <p>Le uniche risorse di terze parti caricate a runtime sono <strong>Google Fonts</strong> (font tipografico Inter) e <strong>YouTube embed</strong> (solo su pagine con video testimonianza).</p>
        <div className="border-l-4 border-[#FF5A1F] bg-[#FF5A1F]/5 p-3 text-xs">
          <strong>Attenzione</strong>: sono previste ma non ancora attive le integrazioni con Meta Ads Pixel, Google Calendar OAuth e WhatsApp Cloud API. Al momento della loro attivazione questo report andrà aggiornato e sarà <strong>obbligatorio implementare un banner cookie con consenso granulare</strong>.
        </div>
      </div>
    ),
  },
  {
    id: "cookies-first",
    number: "2",
    title: "Cookie di prima parte",
    body: (
      <div className="space-y-4 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/50">2.1 Cookie HTTP tradizionali</div>
          <p className="mt-1 text-[#0A0A0A]/85"><strong>Nessun cookie HTTP di prima parte installato.</strong> Il backend FastAPI non emette header <code className="bg-[#0A0A0A]/5 px-1">Set-Cookie</code>. L'autenticazione è gestita esclusivamente via JSON Web Token in <code className="bg-[#0A0A0A]/5 px-1">Authorization: Bearer</code>.</p>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/50 mb-2">2.2 Persistenza in localStorage</div>
          <div className="overflow-x-auto border-2 border-[#0A0A0A]/20">
            <table className="w-full text-xs">
              <thead className="bg-[#0A0A0A] text-[#FAFAF7]">
                <tr>
                  <th className="text-left px-3 py-2 uppercase tracking-widest">Chiave</th>
                  <th className="text-left px-3 py-2 uppercase tracking-widest">Contenuto</th>
                  <th className="text-left px-3 py-2 uppercase tracking-widest">Scopo</th>
                  <th className="text-left px-3 py-2 uppercase tracking-widest">Base giuridica</th>
                  <th className="text-left px-3 py-2 uppercase tracking-widest">Durata</th>
                  <th className="text-left px-3 py-2 uppercase tracking-widest">Categoria</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-[#0A0A0A]/10">
                  <td className="px-3 py-2 font-mono font-bold">perla_token</td>
                  <td className="px-3 py-2">JSON Web Token firmato, valido solo per utenti loggati in /admin o /crm</td>
                  <td className="px-3 py-2">Autenticazione area riservata</td>
                  <td className="px-3 py-2">Necessario tecnico (art. 122 Cod. Privacy — esente da consenso)</td>
                  <td className="px-3 py-2">Fino a logout / max 30 gg</td>
                  <td className="px-3 py-2"><span className="bg-emerald-100 text-emerald-800 px-1.5 py-0.5 text-[10px] uppercase font-bold">Tecnico</span></td>
                </tr>
                <tr className="border-t border-[#0A0A0A]/10">
                  <td className="px-3 py-2 font-mono font-bold">perla_lang</td>
                  <td className="px-3 py-2">Stringa "it" oppure "en"</td>
                  <td className="px-3 py-2">Memorizzare la preferenza lingua utente</td>
                  <td className="px-3 py-2">Necessario tecnico / funzionale (esente da consenso)</td>
                  <td className="px-3 py-2">Persistente fino a cancellazione manuale</td>
                  <td className="px-3 py-2"><span className="bg-blue-100 text-blue-800 px-1.5 py-0.5 text-[10px] uppercase font-bold">Funzionale</span></td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-[#0A0A0A]/60 italic">
            I visitatori anonimi (non loggati) generano SOLO la chiave <code>perla_lang</code> e solo se cambiano manualmente lingua. Nessun tracking anonimo, nessun cookie identificativo persistente.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "third-parties",
    number: "3",
    title: "Cookie e risorse di terze parti",
    body: (
      <div className="space-y-6 text-sm">
        <div>
          <h4 className="font-bold text-lg mb-2 flex items-center gap-2">Google Fonts <span className="text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 px-2 py-0.5 font-bold">Attivo</span></h4>
          <ul className="space-y-1 text-[#0A0A0A]/85 pl-4 list-disc marker:text-[#FF5A1F]">
            <li><strong>Dominio</strong>: fonts.googleapis.com + fonts.gstatic.com</li>
            <li><strong>Risorsa</strong>: font tipografico <em>Inter</em> (peso 600)</li>
            <li><strong>Cookie diretti</strong>: nessuno</li>
            <li><strong>Dato trasmesso a Google</strong>: <strong className="text-amber-700">indirizzo IP dell'utente</strong> ad ogni caricamento pagina</li>
            <li><strong>Frequenza</strong>: ogni page load pubblico (7 pagine + 2 landing)</li>
            <li><strong>Titolare terzo</strong>: Google Ireland Limited</li>
            <li><strong>Trasferimento extra‑UE</strong>: possibile (server Google USA con SCC)</li>
            <li><strong>Base giuridica raccomandata</strong>: interesse legittimo (art. 6.1.f GDPR) con menzione in Privacy Policy</li>
            <li className="text-emerald-800"><strong>Mitigazione consigliata</strong>: self‑hosting del font → rimuove completamente la chiamata a Google (~30 min di lavoro)</li>
          </ul>
        </div>

        <div>
          <h4 className="font-bold text-lg mb-2 flex items-center gap-2">YouTube embed <span className="text-[10px] uppercase tracking-widest bg-red-100 text-red-800 px-2 py-0.5 font-bold">Profilazione</span></h4>
          <ul className="space-y-1 text-[#0A0A0A]/85 pl-4 list-disc marker:text-[#FF5A1F]">
            <li><strong>Dominio</strong>: www.youtube.com (NON in modalità nocookie)</li>
            <li><strong>Risorsa</strong>: player video via <code>&lt;iframe&gt;</code>, presente solo se pubblicate testimonianze video</li>
            <li><strong>Pagine coinvolte</strong>: Home (<code>/</code>), Testimonianze (<code>/testimonianze</code>)</li>
            <li><strong>Cookie installati</strong> al caricamento del player:
              <ul className="ml-4 mt-1 space-y-0.5 list-disc marker:text-[#0A0A0A]/40 text-xs">
                <li><code className="bg-[#0A0A0A]/5 px-1">VISITOR_INFO1_LIVE</code> (~6 mesi) — misurazione uso</li>
                <li><code className="bg-[#0A0A0A]/5 px-1">YSC</code> (sessione) — statistiche video</li>
                <li><code className="bg-[#0A0A0A]/5 px-1">PREF</code> (~2 anni) — preferenze utente</li>
                <li><code className="bg-[#0A0A0A]/5 px-1">CONSENT</code> (~2 anni) — memorizzazione consenso Google</li>
                <li><code className="bg-[#0A0A0A]/5 px-1">__Secure-YEC</code>, <code className="bg-[#0A0A0A]/5 px-1">SOCS</code> — altri cookie Google associati</li>
              </ul>
            </li>
            <li className="text-red-700"><strong>Categoria</strong>: cookie di profilazione / marketing terzi → <strong>richiede consenso esplicito preventivo</strong></li>
            <li className="text-emerald-800"><strong>Mitigazione consigliata</strong>: passare a <code>www.youtube-nocookie.com/embed/...</code> (~5 min) — riduce ma non elimina i cookie</li>
            <li className="text-emerald-800"><strong>Alternativa completa</strong>: caricare il player solo dopo click esplicito su un "poster" (approccio <em>click-to-load</em>)</li>
          </ul>
        </div>

        <div className="border-l-4 border-blue-500 bg-blue-50 p-3 text-xs text-blue-900">
          <strong>Link "solo href"</strong> (nessun caricamento automatico) verso Instagram / Spotify / YouTube / Trustpilot degli artisti: sono semplici hyperlink, non installano nulla finché non cliccati. Nessuna azione richiesta.
        </div>
      </div>
    ),
  },
  {
    id: "analytics-none",
    number: "4",
    title: "Servizi di analytics, marketing e pixel",
    body: (
      <div className="text-sm">
        <div className="border-2 border-emerald-600/40 bg-emerald-50 p-4 mb-4 flex items-start gap-3">
          <CheckCircle2 size={22} className="text-emerald-700 shrink-0" />
          <div>
            <div className="font-bold text-emerald-900">Ad oggi installati: nessuno.</div>
            <div className="text-emerald-800 text-xs mt-1">Verifiche eseguite (Marzo 2026) sul codice sorgente.</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
          {["Google Analytics (Universal + GA4)", "Google Tag Manager", "Meta / Facebook Pixel", "TikTok Pixel", "LinkedIn Insight Tag", "Hotjar", "Microsoft Clarity", "Mixpanel / Segment / Amplitude", "Hubspot / ActiveCampaign", "Mailchimp tracking"].map((s) => (
            <div key={s} className="flex items-center gap-2 text-[#0A0A0A]/70"><span className="text-red-500 font-bold">✕</span> {s}</div>
          ))}
        </div>
      </div>
    ),
  },
  {
    id: "planned",
    number: "5",
    title: "Integrazioni pianificate — NON ancora attive",
    body: (
      <div className="space-y-4 text-sm">
        <div className="border-l-4 border-amber-500 bg-amber-50 p-3 text-xs text-amber-900">
          <AlertTriangle size={14} className="inline mr-1" />
          Al momento dell'attivazione di queste integrazioni, questo report e la Cookie Policy dovranno essere aggiornati.
        </div>
        {[
          {
            title: "Meta Ads Pixel (Facebook + Instagram)",
            when: "Al lancio delle campagne Meta Ads",
            cookies: "_fbp (primo party, 90gg), _fbc (primo party, 90gg), + cookie terze parti facebook.com",
            category: "Profilazione / marketing",
            base: "Consenso esplicito preventivo",
            action: "Installazione banner CMP con consenso granulare per categoria",
          },
          {
            title: "Google Calendar OAuth (solo dipendenti CRM)",
            when: "Al ricevimento delle credenziali Google Cloud",
            cookies: "Nessuno sui visitatori pubblici. Cookie di sessione Google (SID, NID, HSID) SOLO durante flusso OAuth sui dominii accounts.google.com",
            category: "Strettamente necessario per dipendenti (rapporto di lavoro)",
            base: "Contratto di lavoro + consenso individuale del venditore",
            action: "Informativa specifica al dipendente al primo consenso OAuth",
          },
          {
            title: "WhatsApp Cloud API (server-to-server)",
            when: "Al ricevimento delle credenziali Meta WhatsApp Business",
            cookies: "Nessuno (integrazione API server-side)",
            category: "Non installa nulla sul client",
            base: "Consenso lead + esecuzione richiesta pre-contrattuale (art. 6.1.b GDPR)",
            action: "Aggiungere checkbox esplicita 'acconsento a ricevere comunicazioni WhatsApp' al form lead",
          },
        ].map((s) => (
          <div key={s.title} className="border-2 border-[#0A0A0A]/20 bg-[#FAFAF7] p-4">
            <div className="font-bold text-[#0A0A0A]">{s.title}</div>
            <dl className="mt-2 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-xs">
              <dt className="text-[#0A0A0A]/50 uppercase tracking-widest font-bold">Attivazione</dt><dd>{s.when}</dd>
              <dt className="text-[#0A0A0A]/50 uppercase tracking-widest font-bold">Cookie</dt><dd>{s.cookies}</dd>
              <dt className="text-[#0A0A0A]/50 uppercase tracking-widest font-bold">Categoria</dt><dd>{s.category}</dd>
              <dt className="text-[#0A0A0A]/50 uppercase tracking-widest font-bold">Base giuridica</dt><dd>{s.base}</dd>
              <dt className="text-[#0A0A0A]/50 uppercase tracking-widest font-bold">Azione richiesta</dt><dd className="text-[#FF5A1F] font-bold">{s.action}</dd>
            </dl>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: "data-collection",
    number: "6",
    title: "Punti di raccolta dati personali",
    body: (
      <div className="space-y-3 text-sm text-[#0A0A0A]/85">
        <div>
          <div className="font-bold">6.1 Form Contatti (<code>/contatti</code>)</div>
          <div className="text-xs mt-1">Campi: nome, email, telefono (opz.), messaggio. Base: consenso libero + esecuzione richiesta pre‑contrattuale.</div>
        </div>
        <div>
          <div className="font-bold">6.2 Landing Page 1 &amp; 2 (<code>/landing-1</code>, <code>/landing-2</code>)</div>
          <div className="text-xs mt-1">Campi: nome artista, email, telefono, genere, ascoltatori Spotify, obiettivo, budget, IG, regione, messaggio.<br/>Automatici da URL (non digitati): utm_source, utm_campaign, utm_medium, utm_ad, utm_adset, utm_content, utm_term, utm_platform.</div>
        </div>
        <div>
          <div className="font-bold">6.3 CRM (area riservata dipendenti)</div>
          <div className="text-xs mt-1">Non è un punto di raccolta pubblico. Accessibile solo ai dipendenti autenticati. Dati gestiti: anagrafica lead, note commerciali, pipeline, materiali artisti, ruoli/commissioni team.</div>
        </div>
      </div>
    ),
  },
  {
    id: "storage",
    number: "7",
    title: "Storage e localizzazione dei dati",
    body: (
      <div className="text-sm space-y-3">
        <div className="overflow-x-auto border-2 border-[#0A0A0A]/20">
          <table className="w-full text-xs">
            <thead className="bg-[#0A0A0A] text-[#FAFAF7]">
              <tr><th className="text-left px-3 py-2 uppercase tracking-widest">Categoria dato</th><th className="text-left px-3 py-2 uppercase tracking-widest">Where stored</th><th className="text-left px-3 py-2 uppercase tracking-widest">Backup</th></tr>
            </thead>
            <tbody>
              <tr className="border-t border-[#0A0A0A]/10"><td className="px-3 py-2">Lead + note + pipeline CRM</td><td className="px-3 py-2">MongoDB nel container del sito</td><td className="px-3 py-2">Automatico giornaliero</td></tr>
              <tr className="border-t border-[#0A0A0A]/10"><td className="px-3 py-2">Utenti CRM (email + password hash bcrypt)</td><td className="px-3 py-2">MongoDB stessa infrastruttura</td><td className="px-3 py-2">Automatico</td></tr>
              <tr className="border-t border-[#0A0A0A]/10"><td className="px-3 py-2">Contenuti CMS (testi, immagini base64, testimonianze)</td><td className="px-3 py-2">MongoDB stessa infrastruttura</td><td className="px-3 py-2">Automatico</td></tr>
              <tr className="border-t border-[#0A0A0A]/10"><td className="px-3 py-2">Log applicativi</td><td className="px-3 py-2">Filesystem effimero, rotazione automatica</td><td className="px-3 py-2">Non persistente</td></tr>
            </tbody>
          </table>
        </div>
        <p className="text-[#0A0A0A]/85 text-sm">Tutti i dati sono conservati all'interno dell'infrastruttura del sito. <strong>Nessun processor esterno</strong> ha attualmente accesso ai dati. All'attivazione di Meta / Google / WhatsApp quei fornitori diventeranno Responsabili del trattamento (art. 28 GDPR) limitatamente al perimetro definito.</p>
        <p className="text-[#0A0A0A]/85 text-sm"><strong>Trasferimento extra‑UE oggi</strong>: nessuno. Al momento dell'attivazione Meta/Google alcuni dati transiteranno verso server extra-UE con clausole contrattuali standard (SCC) approvate.</p>
      </div>
    ),
  },
  {
    id: "recommendations",
    number: "8",
    title: "Raccomandazioni operative (in ordine di priorità)",
    body: (
      <div className="overflow-x-auto border-2 border-[#0A0A0A]/20 text-sm">
        <table className="w-full text-xs">
          <thead className="bg-[#0A0A0A] text-[#FAFAF7]">
            <tr>
              <th className="text-left px-3 py-2 uppercase tracking-widest">#</th>
              <th className="text-left px-3 py-2 uppercase tracking-widest">Intervento</th>
              <th className="text-left px-3 py-2 uppercase tracking-widest">Effetto</th>
              <th className="text-left px-3 py-2 uppercase tracking-widest">Effort</th>
              <th className="text-left px-3 py-2 uppercase tracking-widest">Quando</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["1", "Self-hosting del font Inter", "Rimuove chiamata a Google Fonts", "~30 min", "Prima deploy"],
              ["2", "YouTube in modalità youtube-nocookie.com", "Riduce cookie di tracking dai video", "~5 min", "Prima deploy"],
              ["3", "Pagine /privacy-policy e /cookie-policy editabili da CMS", "Obbligo di legge", "~1 ora", "Prima deploy"],
              ["4", "Banner informativo cookie (non vincolante)", "Trasparenza + compliance minima", "~1 ora", "Prima deploy"],
              ["5", "CMP (banner consenso granulare) — iubenda/Cookiebot", "Consenso valido per Meta Pixel + YouTube", "~2 ore", "All'attivazione Meta Ads"],
              ["6", "Informativa specifica su form lead con checkbox esplicita", "Consenso valido per WhatsApp/email", "~30 min", "All'attivazione WhatsApp"],
            ].map(([n, i, e, t, w]) => (
              <tr key={n} className="border-t border-[#0A0A0A]/10">
                <td className="px-3 py-2 font-bold">{n}</td>
                <td className="px-3 py-2 font-bold">{i}</td>
                <td className="px-3 py-2">{e}</td>
                <td className="px-3 py-2 text-[#FF5A1F] font-bold">{t}</td>
                <td className="px-3 py-2 text-[#0A0A0A]/60">{w}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ),
  },
  {
    id: "summary-dpo",
    number: "9",
    title: "Riepilogo per il DPO / Legale",
    body: (
      <div className="space-y-3 text-sm">
        <div className="border-l-4 border-emerald-600 bg-emerald-50 p-3">
          <div className="font-bold text-emerald-900">Ad oggi (Marzo 2026, sito in staging pre‑deploy):</div>
          <ul className="mt-2 space-y-1 text-emerald-900/85 text-xs">
            <li>✓ Non serve banner cookie con consenso vincolante — nessun cookie di profilazione attivo</li>
            <li>✓ Serve informativa privacy con menzione di Google Fonts (interesse legittimo) e YouTube embed (se attivi)</li>
            <li>✓ Servono i punti 1–4 delle raccomandazioni implementati prima del go‑live</li>
          </ul>
        </div>
        <div className="border-l-4 border-amber-500 bg-amber-50 p-3">
          <div className="font-bold text-amber-900">Al momento dell'attivazione di Meta Ads Pixel e/o WhatsApp Cloud API:</div>
          <div className="mt-1 text-amber-900/85 text-xs">Diventerà obbligatorio un banner CMP con consenso granulare per categoria (essenziali / preferenze / analytics / marketing) e blocco preventivo dei tag di terze parti fino al consenso.</div>
        </div>
      </div>
    ),
  },
];

export default function AdminPrivacyReport() {
  const download = () => {
    const blob = new Blob([REPORT_MD], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `report-cookie-tracker-perla-music-${new Date().toISOString().slice(0, 10)}.md`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Report scaricato");
  };
  const printPDF = () => {
    window.print();
  };

  return (
    <div className="p-6 lg:p-10 max-w-5xl print:p-0" data-testid="admin-privacy-report-page">
      {/* Toolbar - hidden in print */}
      <div className="mb-8 print:hidden">
        <div className="text-xs font-bold uppercase tracking-widest text-[#FF5A1F]">— Compliance</div>
        <h1 className="font-display text-5xl uppercase text-[#0A0A0A] mt-2 flex items-center gap-3">
          <ShieldCheck size={38} className="text-[#FF5A1F]" strokeWidth={1.5} /> Report Privacy
        </h1>
        <p className="text-[#0A0A0A]/60 mt-2 max-w-2xl leading-relaxed">
          Report tecnico completo di cookie e tracker installati sul sito, aggiornato al codice attuale. Da girare al DPO / legale per la redazione di Privacy Policy e Cookie Policy.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <button onClick={download} className="btn-cta py-2 px-4 text-xs" data-testid="privacy-report-download">
            <Download size={14} /> Scarica .md
          </button>
          <button onClick={printPDF} className="btn-outline-dark py-2 px-4 text-xs" data-testid="privacy-report-print">
            <Printer size={14} /> Stampa / Salva PDF
          </button>
        </div>
      </div>

      {/* Print-only header */}
      <div className="hidden print:block mb-6">
        <h1 className="font-display text-3xl uppercase">Report Cookie &amp; Tracker — Perla Music</h1>
        <div className="text-xs mt-1">Documento tecnico per Privacy Policy e Cookie Policy · Versione 1.0 · Marzo 2026</div>
      </div>

      {/* Metadata */}
      <div className="border-2 border-[#0A0A0A] bg-[#FAFAF7] p-4 mb-8 grid grid-cols-2 gap-3 text-xs">
        <div><span className="text-[#0A0A0A]/50 uppercase tracking-widest font-bold">Versione</span><div className="font-mono">1.0</div></div>
        <div><span className="text-[#0A0A0A]/50 uppercase tracking-widest font-bold">Data</span><div className="font-mono">Marzo 2026</div></div>
        <div><span className="text-[#0A0A0A]/50 uppercase tracking-widest font-bold">Stato sito</span><div className="font-mono">Staging (pre-deploy)</div></div>
        <div><span className="text-[#0A0A0A]/50 uppercase tracking-widest font-bold">URL preview</span><div className="font-mono text-[10px] break-all">perla-mgmt-stage.preview.emergentagent.com</div></div>
      </div>

      {/* Sections */}
      <div className="space-y-8">
        {SECTIONS.map((s) => (
          <section key={s.id} className="break-inside-avoid" data-testid={`report-section-${s.id}`}>
            <div className="mb-4 pb-2 border-b-2 border-[#0A0A0A]">
              <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold">Sezione {s.number}</div>
              <h2 className="font-display text-3xl uppercase text-[#0A0A0A] mt-1">{s.title}</h2>
            </div>
            {s.body}
          </section>
        ))}
      </div>

      <div className="mt-12 pt-6 border-t-2 border-[#0A0A0A]/10 text-xs text-[#0A0A0A]/50 italic text-center print:mt-6">
        Fine del documento. Per chiarimenti tecnici contattare il team di sviluppo. Codice sorgente disponibile per audit su richiesta del Titolare.
      </div>

      <style>{`@media print { body { background: white; } .btn-cta, .btn-outline-dark { display: none !important; } }`}</style>
    </div>
  );
}
